import {
    useCallback, useDeferredValue, useEffect, useMemo, useState,
} from 'react';
import apiService, { isApiAbortError } from '../services/apiService';
import {
    ALL_RANGE, DEFAULT_RANGE, EMPTY_FILTERS, URL_KEYS, parseCompsUrl, serializeCompsUrl,
} from '../utils/compsSources';

// Module-level loaders, so each hook's effect has a stable dependency.
const LOADERS = {
    sold: (stock, opts) => apiService.getMarketSource(stock, 'sold', opts),
    auction: (stock, opts) => apiService.getMarketSource(stock, 'auction', opts),
    advertised: (stock, opts) => apiService.getMarketSource(stock, 'advertised', opts),
    own: (stock, opts) => apiService.getOwnSales(stock, opts),
};

const LOADING = Object.freeze({ status: 'loading', data: null });

/**
 * One source, fetched on its own: its own loading, error and Retry, and an
 * AbortController so a unit switch (or unmount) cancels the request. The
 * state is tagged with the stock it belongs to, so a late answer for the
 * previous unit can never show on the next one.
 */
function useOneSource(stock, key) {
    const [state, setState] = useState({ stock: null, attempt: -1, ...LOADING });
    const [attempt, setAttempt] = useState(0);
    useEffect(() => {
        if (!stock) return undefined;
        const controller = new AbortController();
        let live = true;
        setState({ stock, attempt, ...LOADING });
        LOADERS[key](stock, { signal: controller.signal })
            .then((data) => { if (live) setState({ stock, attempt, status: 'done', data }); })
            .catch((error) => {
                if (live && !isApiAbortError(error)) setState({ stock, attempt, status: 'error', data: null });
            });
        return () => {
            live = false;
            controller.abort();
        };
    }, [stock, key, attempt]);
    const retry = useCallback(() => setAttempt((a) => a + 1), []);
    const current = state.stock === stock && state.attempt === attempt ? state : LOADING;
    return useMemo(() => ({ status: current.status, data: current.data, retry }), [current.status, current.data, retry]);
}

/**
 * The four sources of the Comps tab, in parallel and independent: a slow or
 * failing one never holds the others back.
 */
export function useCompsSources(stock) {
    const sold = useOneSource(stock, 'sold');
    const auction = useOneSource(stock, 'auction');
    const advertised = useOneSource(stock, 'advertised');
    const own = useOneSource(stock, 'own');
    return useMemo(() => ({
        sold, auction, advertised, own,
    }), [sold, auction, advertised, own]);
}

const URL_WRITE_MS = 300;
const SESSION_KEY = (stock) => `pp_comps_view:${stock}`;

const currentSearch = () => (typeof window === 'undefined' ? '' : window.location.search);

/**
 * Where the view starts: the address when it carries comps params (reload,
 * shared link); else what this tab last showed for the unit (the unit page
 * drops the params when the user visits another tab and comes back).
 */
function initialView(stock) {
    const search = currentSearch();
    let fromUrl = false;
    try {
        const params = new URLSearchParams(search);
        fromUrl = URL_KEYS.some((k) => params.has(k));
    } catch (e) { /* treat as empty */ }
    if (!fromUrl && stock) {
        try {
            const saved = window.sessionStorage.getItem(SESSION_KEY(stock));
            if (saved) return parseCompsUrl(saved);
        } catch (e) { /* storage blocked: start fresh */ }
    }
    return parseCompsUrl(search);
}

/**
 * The selected card, list sort and filters, restored from the address on
 * mount and written back (debounced, replaceState, the router's history
 * state kept) so a reload or a shared link shows the same view; also kept
 * per unit in sessionStorage for a return from another tab. Computation runs
 * on the deferred filters, so typing in the hours fields stays smooth.
 *
 * The automatic "All" fallback (applyAutoRange) is applied once and kept,
 * without counting as the user's choice: it is not written to the address
 * and Reset all re-evaluates it.
 */
export function useCompsView(stock) {
    const initial = useMemo(() => initialView(stock), []); // eslint-disable-line react-hooks/exhaustive-deps
    const [view, setView] = useState(initial.view);
    const [sort, setSort] = useState(initial.sort);
    const [filters, setFiltersRaw] = useState(initial.filters);
    // Did the user (or the address) pick the date range? Until then the
    // default window may fall back to All (utils/compsSources autoRange).
    const [rangeChosen, setRangeChosen] = useState(initial.rangeChosen);
    const [autoApplied, setAutoApplied] = useState(false);
    const deferredFilters = useDeferredValue(filters);
    const setFilters = useCallback((next) => {
        setFiltersRaw((f) => {
            const out = typeof next === 'function' ? next(f) : next;
            if (out.range !== f.range) setRangeChosen(true);
            return out;
        });
    }, []);
    const autoShown = autoApplied && !rangeChosen && filters.range === ALL_RANGE;

    useEffect(() => {
        if (typeof window === 'undefined') return undefined;
        const t = setTimeout(() => {
            // An explicit range (even the default 12 months) is written; the
            // automatic fallback is not.
            const state = {
                view, sort, filters: autoShown ? { ...filters, range: DEFAULT_RANGE } : filters, rangeChosen: rangeChosen && !autoShown,
            };
            const next = serializeCompsUrl(window.location.search, state);
            if (next !== window.location.search) {
                try {
                    window.history.replaceState(window.history.state, '', `${window.location.pathname}${next}${window.location.hash}`);
                } catch (e) { /* a sandboxed frame may refuse; the view still works */ }
            }
            if (stock) {
                try { window.sessionStorage.setItem(SESSION_KEY(stock), serializeCompsUrl('', state)); } catch (e) { /* ignore */ }
            }
        }, URL_WRITE_MS);
        return () => clearTimeout(t);
    }, [view, sort, filters, autoShown, rangeChosen, stock]);

    const patchFilters = useCallback(
        (patch) => setFilters((f) => ({ ...f, ...(typeof patch === 'function' ? patch(f) : patch) })),
        [setFilters],
    );
    // A range picked in the rail counts as chosen even when it equals the
    // stored one.
    const chooseRange = useCallback((range) => {
        setRangeChosen(true);
        setFiltersRaw((f) => (f.range === range ? f : { ...f, range }));
    }, []);
    const applyAutoRange = useCallback(() => {
        setAutoApplied(true);
        setFiltersRaw((f) => (f.range === DEFAULT_RANGE ? { ...f, range: ALL_RANGE } : f));
    }, []);
    const resetFilters = useCallback(() => {
        setFiltersRaw(EMPTY_FILTERS);
        setRangeChosen(false);
        setAutoApplied(false);
    }, []);

    return {
        view,
        setView,
        sort,
        setSort,
        filters,
        deferredFilters,
        setFilters,
        patchFilters,
        resetFilters,
        rangeChosen,
        chooseRange,
        applyAutoRange,
        autoShown,
    };
}
